import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  MessageSquareText,
  Users,
  FolderKanban,
  ClipboardList,
  FileText,
  Wand2,
  ChevronDown,
  BadgeDollarSign,
  Briefcase,
  Award,
  Activity,
  ChevronRight,
  LayoutDashboard,
  Building2,
  TrendingUp,
  Zap,
  Layers,
  Receipt,
  Tag,
  ListChecks,
  Target,
  GraduationCap,
  Settings as SettingsIcon,
  Wallet,
  ChevronLeft,
  Gavel,
  Inbox,
  BarChart3,
  Settings2,
  FolderOpen,
} from "lucide-react";
import { useAuthStore } from "../../store/useAuthStore";
import { useChatStore } from "../../store/useChatStore";

type Role = "Admin" | "Supervisor" | "Sales Director" | "Business Developer" | "Client" | "Finance User";

type NavItem = {
  name: string;
  icon: React.ElementType;
  path: string;
};

type NavSection = {
  label: string;
  icon?: React.ElementType; // optional small icon shown beside the section header
  items: NavItem[];
};

// Admin nav mirrors the Business Developer common structure (same groups &
// order) so both portals share one navigation. Admin-only pages are nested
// inside the matching groups so nothing is lost.
// Approved CRE navigation hierarchy (14 Aug). Admin mirrors the same structure
// with admin-scoped routes; both portals share one navigation.
const ADMIN_SECTIONS: NavSection[] = [
  {
    label: "",
    items: [
      { name: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
      { name: "Inbox", icon: Inbox, path: "/chat" },
      { name: "Contacts", icon: Users, path: "/admin/clients" },
      { name: "Upwork Jobs", icon: Briefcase, path: "/upwork-jobs" },
      { name: "Job Records", icon: Award, path: "/freelancer-job-records" },
    ],
  },
  {
    label: "Operations",
    icon: Settings2,
    items: [
      { name: "Bidding", icon: Gavel, path: "/bids" },
      { name: "Proposals", icon: FileText, path: "/proposals" },
      { name: "Questionnaires", icon: ClipboardList, path: "/questionnaires" },
      { name: "Projects", icon: FolderKanban, path: "/admin/projects" },
      { name: "Activities", icon: ListChecks, path: "/activities" },
    ],
  },
  {
    label: "Finance",
    icon: Wallet,
    items: [
      { name: "Invoices", icon: Receipt, path: "/admin/invoices" },
      { name: "Commissions", icon: BadgeDollarSign, path: "/admin/commissions" },
    ],
  },
  {
    label: "",
    items: [
      { name: "Interview Prep", icon: GraduationCap, path: "/interview-prep" },
      { name: "Files", icon: FolderOpen, path: "/files" },
    ],
  },
  {
    label: "Analytics",
    icon: BarChart3,
    items: [
      { name: "Reports & Analytics", icon: BarChart3, path: "/reports" },
      { name: "Performance", icon: TrendingUp, path: "/admin/performance" },
      { name: "Account KPIs", icon: Zap, path: "/admin/account-performance" },
    ],
  },
  {
    label: "",
    items: [
      { name: "Bidding Config", icon: Gavel, path: "/admin/bidding-config" },
      { name: "Settings", icon: SettingsIcon, path: "/settings" },
    ],
  },
];

const BD_SECTIONS: NavSection[] = [
  {
    label: "",
    items: [
      { name: "Dashboard", icon: LayoutDashboard, path: "/bd/dashboard" },
      { name: "Inbox", icon: Inbox, path: "/chat" },
      { name: "Contacts", icon: Users, path: "/clients" },
      { name: "Upwork Jobs", icon: Briefcase, path: "/upwork-jobs" },
      { name: "Job Records", icon: Award, path: "/freelancer-job-records" },
    ],
  },
  {
    label: "Operations",
    icon: Settings2,
    items: [
      { name: "Bidding", icon: Gavel, path: "/bids" },
      { name: "Proposals", icon: FileText, path: "/proposals" },
      { name: "Questionnaires", icon: ClipboardList, path: "/questionnaires" },
      { name: "Projects", icon: FolderKanban, path: "/projects" },
      { name: "Activities", icon: ListChecks, path: "/activities" },
    ],
  },
  {
    label: "Finance",
    icon: Wallet,
    items: [
      { name: "Invoices", icon: Receipt, path: "/bd/invoices" },
      { name: "Commissions", icon: BadgeDollarSign, path: "/bd/commissions" },
    ],
  },
  {
    label: "",
    items: [
      { name: "Interview Prep", icon: GraduationCap, path: "/interview-prep" },
      { name: "Files", icon: FolderOpen, path: "/bd/files" },
    ],
  },
  {
    label: "Analytics",
    icon: BarChart3,
    items: [
      { name: "Reports & Analytics", icon: BarChart3, path: "/reports" },
      { name: "Daily Report", icon: Activity, path: "/reports/daily" },
    ],
  },
];

const CLIENT_SECTIONS: NavSection[] = [
  {
    label: "Workspace",
    items: [
      { name: "Conversation", icon: MessageSquareText, path: "/chat" },
      { name: "Projects", icon: FolderKanban, path: "/projects" },
    ],
  },
  {
    label: "Documents",
    items: [
      { name: "Proposals", icon: FileText, path: "/proposals" },
      { name: "Questionnaires", icon: ClipboardList, path: "/questionnaires" },
    ],
  },
];

const FINANCE_SECTIONS: NavSection[] = [
  {
    label: "Finance",
    items: [
      { name: "Projects", icon: FolderKanban, path: "/projects" },
      { name: "Invoices", icon: Receipt, path: "/admin/invoices" },
      { name: "Commissions", icon: BadgeDollarSign, path: "/admin/commissions" },
    ],
  },
];

const getSections = (role: Role | undefined): NavSection[] => {
  switch (role) {
    case "Admin":
    case "Supervisor":
    case "Sales Director":
      return ADMIN_SECTIONS;
    case "Client":
      return CLIENT_SECTIONS;
    case "Finance User":
      return FINANCE_SECTIONS;
    case "Business Developer":
    default:
      return BD_SECTIONS;
  }
};

const Sidebar = ({
  mobileOpen = false,
  onClose,
  collapsed = false,
  onToggle,
}: {
  mobileOpen?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  onToggle?: () => void;
}) => {
  const { authUser } = useAuthStore();
  const sections = getSections(authUser?.role as Role | undefined);

  // Live total unread across conversations — badge on the Inbox nav item.
  const { users: chatUsers, getUsers, subscribeSidebar, unsubscribeSidebar } = useChatStore();
  useEffect(() => {
    getUsers();
    subscribeSidebar();
    return () => unsubscribeSidebar();
  }, [getUsers, subscribeSidebar, unsubscribeSidebar]);
  const totalUnread = chatUsers.reduce((s, u) => s + (u.unreadCount || 0), 0);
  // `mini` styling only kicks in at lg+ (desktop); the mobile drawer stays full.
  const mini = collapsed;
  // Collapsible nav groups (Operations, Finance, Analytics…). Default: every
  // group is COLLAPSED except "Operations" — the user's explicit toggles then
  // override that default. In mini mode groups flatten to icons (no-op there).
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>({});
  const isGroupClosed = (label: string) =>
    label in closedGroups ? closedGroups[label] : label !== "Operations";
  const toggleGroup = (label: string) =>
    setClosedGroups((s) => {
      const currentlyClosed = label in s ? s[label] : label !== "Operations";
      return { ...s, [label]: !currentlyClosed };
    });

  return (
    <aside
      className={`w-64 h-screen fixed left-0 top-0 flex flex-col text-on-surface
      bg-surface-container-low border-r border-outline-variant z-50 transform transition-all duration-300
      lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:transform-none
      ${mini ? "lg:w-20" : "lg:w-56 2xl:w-64"}`}
    >
      {/* Collapse toggle (desktop only) */}
      {onToggle && (
        <button
          onClick={onToggle}
          title={mini ? "Expand sidebar" : "Collapse sidebar"}
          className="hidden lg:grid absolute -right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-surface-container-lowest border border-outline-variant shadow-sm place-items-center text-on-surface-variant hover:text-primary hover:bg-surface-container z-50"
        >
          {mini ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}
      <div className={`px-5 pt-6 pb-5 lg:pt-5 lg:pb-4 2xl:px-5 2xl:pt-6 2xl:pb-5 border-b border-outline-variant ${mini ? "lg:px-2" : "lg:px-4"}`}>
        <NavLink to="/" className={`flex items-center gap-3 lg:gap-2.5 group ${mini ? "lg:justify-center" : ""}`}>
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-[#0b5fd6] grid place-items-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="12" r="5.5" stroke="white" strokeWidth="2" opacity="0.7" />
              <circle cx="15" cy="12" r="5.5" stroke="white" strokeWidth="2" />
            </svg>
          </div>
          <div className={`flex flex-col leading-tight ${mini ? "lg:hidden" : ""}`}>
            <span className="text-lg font-black tracking-tight text-on-surface">Omni Workspace</span>
            <span className="text-[11px] font-semibold text-on-surface-variant">by DymaxTech</span>
          </div>
        </NavLink>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pt-4 pb-4 space-y-4 cre-scroll">
        {sections.map((section, si) => {
          const SIcon = section.icon;
          const groupClosed = !!section.label && isGroupClosed(section.label);
          return (
            <div key={section.label || `sec-${si}`}>
              {section.label && (
                <button
                  onClick={() => toggleGroup(section.label)}
                  className={`w-full mb-0.5 flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-highest transition-colors ${mini ? "lg:hidden" : ""}`}
                >
                  <span className="flex items-center gap-3">{SIcon && <SIcon size={20} className="shrink-0" />} {section.label}</span>
                  <ChevronDown size={16} className={`transition-transform ${groupClosed ? "-rotate-90" : ""}`} />
                </button>
              )}
              <div className={`space-y-0.5 ${section.label && !mini ? "pl-4" : ""} ${groupClosed && !mini ? "hidden" : ""}`}>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => onClose?.()}
                      title={mini ? item.name : undefined}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2.5 lg:py-2 text-sm font-medium transition-colors ${mini ? "lg:justify-center lg:px-0" : ""} ${
                          isActive
                            ? "bg-primary text-white"
                            : "text-on-surface-variant hover:bg-surface-container-highest"
                        }`
                      }
                    >
                      <Icon size={18} className="shrink-0" />
                      <span className={`flex-1 truncate ${mini ? "lg:hidden" : ""}`}>{item.name}</span>
                      {item.name === "Inbox" && totalUnread > 0 && (
                        <span className={`min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shrink-0 ${mini ? "lg:absolute lg:top-1 lg:right-1" : ""}`}>
                          {totalUnread > 99 ? "99+" : totalUnread}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className={`py-4 border-t border-outline-variant ${mini ? "lg:px-2" : "px-4"}`}>
        <div className={`rounded-xl bg-surface-container px-3 py-2.5 flex items-center gap-2.5 ${mini ? "lg:justify-center lg:px-0" : ""}`}>
          <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
          <div className={`flex-1 leading-tight ${mini ? "lg:hidden" : ""}`}>
            <p className="text-[11px] font-semibold text-on-surface">All systems operational</p>
            <p className="text-[9px] text-on-surface-variant uppercase tracking-[0.15em]">Powered by DymaxTech</p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;


